import { createPortal } from 'react-dom';

import PropTypes from 'prop-types';

import styles from './MobileInputModal.module.scss';

/**
 * 모바일 입력 모달 컴포넌트 (바텀시트 스타일)
 * - 피그마 디자인: 아래에서 올라오는 바텀시트
 * - 수정요청, 반려, 코멘트 작성 등에 사용
 * @param {number} maxLength 최대 글자수 (기본값 500)
 * @param {Function} onExceedMaxLength 붙여넣기로 최대 글자수 초과 시 콜백
 */
function MobileInputModal({
  isOpen,
  title,
  value,
  onChange,
  onConfirm,
  onCancel,
  placeholder,
  maxLength = 500,
  onExceedMaxLength,
}) {
  // maxLength 기본값 보장 (defaultProps가 적용되지 않는 경우 대비)
  const effectiveMaxLength = maxLength || 500;

  if (!isOpen) {
    return null;
  }

  function handleBackdropClick(event) {
    // 백드롭 클릭 시 닫지 않음 (확인/취소 버튼으로만 닫힘)
    event.stopPropagation();
  }

  // 붙여넣기 이벤트 핸들러 - 클립보드 데이터 직접 확인
  function handlePaste(event) {
    if (!onChange) return;

    const pasteData = event.clipboardData?.getData('text') || '';
    const currentValue = value || '';

    // 선택 영역 계산 (선택된 텍스트는 대체됨)
    const target = event.target;
    const selectionStart = target.selectionStart || 0;
    const selectionEnd = target.selectionEnd || 0;

    // 붙여넣기 후 예상 텍스트
    const beforeSelection = currentValue.substring(0, selectionStart);
    const afterSelection = currentValue.substring(selectionEnd);
    const expectedValue = beforeSelection + pasteData + afterSelection;

    // 최대 글자수 초과 시
    if (expectedValue.length > effectiveMaxLength) {
      event.preventDefault(); // 붙여넣기 차단
      if (onExceedMaxLength) {
        onExceedMaxLength();
      }
    }
  }

  // 텍스트 변경 핸들러 - 타이핑 시 제한
  function handleTextareaChange(event) {
    if (!onChange) return;

    const newValue = event.target.value;

    // 최대 글자수 이하만 허용
    if (newValue.length <= effectiveMaxLength) {
      onChange(newValue);
    }
    // 초과 시 무시 (타이핑의 경우 알럿 없이 차단)
  }

  const currentLength = value ? value.length : 0;

  return createPortal(
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.bottomSheet}>
        {/* 헤더 */}
        <div className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
        </div>

        {/* 입력 영역 */}
        <div className={styles.content}>
          <textarea
            className={styles.textarea}
            value={value}
            onChange={handleTextareaChange}
            onPaste={handlePaste}
            placeholder={placeholder}
            rows={5}
            autoFocus
          />
          <div className={styles.charCounter}>
            <span className={currentLength >= effectiveMaxLength ? styles.limitReached : ''}>
              {currentLength}
            </span>
            /{effectiveMaxLength}자
          </div>
        </div>

        {/* 버튼 영역 */}
        <div className={styles.footer}>
          <div className={styles.buttonGroup}>
            <button type="button" className={styles.actionButton} onClick={onCancel}>
              취소
            </button>
            <button
              type="button"
              className={`${styles.actionButton} ${styles.primaryButton}`}
              onClick={onConfirm}
            >
              확인
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

MobileInputModal.propTypes = {
  isOpen: PropTypes.bool,
  title: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  onExceedMaxLength: PropTypes.func,
};

MobileInputModal.defaultProps = {
  isOpen: false,
  title: '',
  value: '',
  onChange: null,
  onConfirm: null,
  onCancel: null,
  placeholder: '내용을 입력해주세요.',
  maxLength: 500,
  onExceedMaxLength: null,
};

export default MobileInputModal;
